import { KeyboardArrowRightTwoTone } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as api from '../api';
import {
  getOrderInfoPaymentTransaction,
  getOrderInfoSalesOrderAccepter,
  getOrderInfoSalesOrderItem,
} from '../api';
import LoadingSpinner from '../components/LoadingSpinner';
import OrderInfoPaymentTransaction from '../components/OrderInfoPaymentTransaction';
import OrderInfoSalesOrderAccepter from '../components/OrderInfoSalesOrderAccepter';
import OrderInfoSalesOrderItem from '../components/OrderInfoSalesOrderItem';
import { AuthContext } from '../context/AuthContext';
import { BalanceContext } from '../context/BalanceContext';
import { formatAmountInCurrency } from '../utils';
import {
  IOrderInfoPaymentTransaction,
  IOrderInfoSalesOrderAccepter,
  IOrderInfoSalesOrderItem,
} from '../utils/types';

type OrderInfosContainerProps = {
  salesIndividualId: string;
  lastName: string;
  orderNumber: string;
};

export function OrderInfosContainer({
  salesIndividualId,
  lastName,
  orderNumber,
}: OrderInfosContainerProps) {
  const [salesOrderItem, setSalesOrderItem] = useState<
    IOrderInfoSalesOrderItem[]
  >([]);
  const [salesOrderAccepter, setSalesOrderAccepter] = useState<
    IOrderInfoSalesOrderAccepter[]
  >([]);
  const [paymentTransaction, setPaymentTransaction] =
    useState<IOrderInfoPaymentTransaction>();

  const { token } = useContext(AuthContext);

  const { t } = useTranslation();

  const [error, setError] = useState<string>();
  const nav = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);
  const { setBalances } = useContext(BalanceContext);

  const handlePayClick = async (vals: {
    lastName: string;
    orderNumber: string;
  }) => {
    try {
      setSubmitting(true);
      const res = await api.balances(vals);
      const updatedBalances = await Promise.all(
        res.map(async (item) => {
          //initial toPay fill
          let toPay = {
            amount: item.payment.balance.amount,
            currency: { id: 724, code: 'TRY', name: 'Turkish Lira' },
            currencyExchangeRate: 0,
          } as api.ApiAmountWithCurrency;

          if (toPay.amount > 0)
            toPay = await api.currencyExchange(
              item.payment.balance.currency.code,
              'TRY',
              item.payment.balance.amount,
            );

          return {
            ...item,
            payment: {
              ...item.payment,
              loyalty: {
                points: 0,
                fiat: 0,
              },
              received: {
                amount:
                  (item.payment.total.amount || 0) -
                  (item.payment.balance.amount || 0),
                currency: item.payment.balance.currency,
              },
              toPay: {
                amount: toPay?.amount || 0,
                currencyExchangeRate:
                  item.payment.balance.amount > 0
                    ? item.payment.balance.amount / toPay?.amount
                    : 0, // 1 TRY => ?€ | ?$...
                // todo implement company currency
                currency: toPay.currency || null,
              },
            },
          };
        }),
      );

      setBalances(updatedBalances);
      nav('/balances');
      setSubmitting(false);
    } catch (e: any) {
      setError(
        e.detail || e.message || t('balancesQueryPage.balancesNotFound'),
      );
      setSubmitting(false);
    }
  };

  useEffect(() => {
    fetchOrderInfoSalesOrderItem(salesIndividualId);
    fetchOrderInfoSalesOrderAccepter(salesIndividualId);
    fetchOrderInfoPaymentTransaction(salesIndividualId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesIndividualId]);

  const fetchOrderInfoSalesOrderItem = async (id: string) => {
    try {
      const res = await getOrderInfoSalesOrderItem(id, token);
      const data = await res.json();
      if (res?.status === 200) {
        setSalesOrderItem(data);
      } else {
        throw data;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderInfoSalesOrderAccepter = async (id: string) => {
    try {
      const res = await getOrderInfoSalesOrderAccepter(id, token);
      const data = await res.json();
      if (res?.status === 200) {
        setSalesOrderAccepter(data);
      } else {
        throw data;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderInfoPaymentTransaction = async (id: string) => {
    try {
      const res = await getOrderInfoPaymentTransaction(id, token);
      const data = await res.json();
      if (res?.status === 200) {
        setPaymentTransaction(data);
      } else {
        throw data;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <CircularProgress color="secondary" />;
  }
  const totalSalesOrderAmount = salesOrderItem.reduce((total, item) => {
    const itemAmount =
      item.pricing.amount -
      item.discounts.reduce((discountTotal, discount) => {
        const discountAmount =
          typeof discount.amount === 'number'
            ? discount.amount
            : parseFloat(discount.amount) || 0;
        return discountTotal + discountAmount;
      }, 0);
    return total + itemAmount;
  }, 0);

  // paymentTransaction'daki bütün ödemelerin toplamını hesapla
  const totalPaymentAmount =
    paymentTransaction?.paymentTransactions.reduce(
      (total, paymentTransactionItem) =>
        total + parseFloat(paymentTransactionItem.amount || '0'),
      0,
    ) || 0; // Default to 0 if paymentTransaction is undefined
  const remainingAmount = (totalSalesOrderAmount - totalPaymentAmount).toFixed(
    2,
  );
  const currencyCode = salesOrderItem.map(
    (salesOrderItem) => salesOrderItem.pricing.currency.code,
  );

  return (
    <Box>
      {submitting && (
        <LoadingSpinner loadingText={t('accountContainer.submitting')} />
      )}

      <OrderInfoSalesOrderAccepter
        salesOrderAccepter={salesOrderAccepter}
        salesIndividualId={salesIndividualId}
      />

      <Box sx={{ mt: 4 }} />

      <OrderInfoSalesOrderItem
        salesOrderItem={salesOrderItem}
        salesIndividualId={salesIndividualId}
      />

      {(totalSalesOrderAmount - totalPaymentAmount || 0) > 0 && (
        <Paper sx={{ mt: 3, border: 'none' }}>
          <Alert severity="warning" sx={{ fontSize: '1.1rem' }}>
            <Typography>
              {t('orderInfos.remainingPayment', {
                amount: formatAmountInCurrency(
                  Number(remainingAmount),
                  currencyCode[0],
                ),
              })}
            </Typography>
            <Button
              size="small"
              color="secondary"
              onClick={() =>
                handlePayClick({
                  lastName,
                  orderNumber,
                })
              }
              disabled={submitting}
              sx={{ mt: 0.5 }}
            >
              {t('orderInfos.pay')}
              <KeyboardArrowRightTwoTone />
            </Button>
          </Alert>
        </Paper>
      )}

      <Typography variant="h5" sx={{ mt: 3, mb: 2 }}>
        {t('orderInfos.payment')}
      </Typography>

      <OrderInfoPaymentTransaction paymentTransaction={paymentTransaction} />
    </Box>
  );
}
